import { ICarbonEmissionAverage } from 'common/emissions';
import { IItinerary, IKeyAnyValue, IKeyStringValue } from 'common/types';
import { TripType } from 'contexts/searchParams';

export type OBJ = { [key: string]: any };

export enum Popovers {
  none = 'none',
  selectType = 'selectType',
  sortBy = 'sortBy',
  payMethodFilter = 'payMethodFilter',
  similar = 'similar',
}

export enum SelectTypeKeys {
  indivisual = 'indivisual',
  concurrent = 'concurrent',
  none = 'none',
}

export interface IQueryValue {
  [index: string]: any;
  fareType: string;
  isDirect?: boolean;
  itinerary: IItinerary[];
  adult: number;
  child: number;
  infant: number;
  selectType?: string;
  selectedFlight?: string;
  stayLength?: string;
  trip: TripType;
  where?: string;
  galileoFlag: boolean;
  galileoKey: string;
  travelBizFlag: boolean;
  travelBizKey: string;
}

export enum ScheduleKeys {
  startAirport = 'sa', // 출발지
  endAirport = 'ea', // 도착지
  airlineVendor = 'av', // 항공사
  flightNumber = 'fn', // 비행기번호
  startDateTime = 'sdt', // 출발시간(YYYYMMDDHHmm)
  endDateTime = 'edt', // 도착시간(YYYYMMDDHHmm)
  cooperatedAirlineVendor = 'oav', // 공동운항 항공사(아시아나로 예약했는데 대한항공 타는 경우 같은..)
  jetTime = 'jt', // 운항시간(HHmm)
  flightTime = 'ft', // 비행시간(HHmm)
  waitingTime = 'ct', // 대기시간(HHmm)
  isMain = 'im', // 메인 항공사 여부 true/false(공동운항)
  planeInfo = 'et',
  hiddenAirports = 'ha',
  carbonEmission = 'carbonEmission',
}

interface IHiddenAirport {
  ap: string;
  st: string;
}

export interface IScheduleDetail {
  [ScheduleKeys.startAirport]: string;
  [ScheduleKeys.endAirport]: string;
  [ScheduleKeys.airlineVendor]: string;
  [ScheduleKeys.flightNumber]: string;
  [ScheduleKeys.cooperatedAirlineVendor]: string;
  [ScheduleKeys.jetTime]: string;
  [ScheduleKeys.flightTime]: string;
  [ScheduleKeys.waitingTime]: string;
  [ScheduleKeys.isMain]: boolean;
  [ScheduleKeys.startDateTime]: string;
  [ScheduleKeys.endDateTime]: string;
  [ScheduleKeys.planeInfo]?: string;
  [ScheduleKeys.hiddenAirports]?: IHiddenAirport[];
  [ScheduleKeys.carbonEmission]?: number;
}

export interface ISchedule {
  id: string;
  detail: IScheduleDetail[];
  journeyTime: string[];
}

export interface ISchedules {
  [key: string]: ISchedule;
}

export interface IFare {
  Fare: number; // 기본 요금
  NaverFare: number; // 네이버 프로모션 요금
  Tax: number; // 세금
  QCharge: number; // 유류세
}

interface ILandingUrl {
  '#cdata-section': string;
}

export enum FareTarget {
  Adult = 'Adult',
  Child = 'Child',
  Infant = 'Infant',
}

export enum BaggageTypeCode {
  CHARGE = 'C',
  FREE = 'F',
  PARTIAL_CHARGE = 'P',
}

export enum ConfirmTypeCode {
  CONFIRMED = 'Y',
  UNCONFIRMED = 'N',
}

export interface IFareDetail {
  [FareTarget.Adult]: IFare;
  [FareTarget.Child]: IFare;
  [FareTarget.Infant]: IFare;
  ReserveParameter: ILandingUrl;
  PromotionParameter: ILandingUrl;
  priceTransparencyUrl?: ILandingUrl;
  FareType: string;
  AgtCode: string;
  ConfirmType?: ConfirmTypeCode;
  BaggageType?: BaggageTypeCode;
  sch: string[];
  hash?: string;
  // 로그인 상태이고 멤버쉽 요금이 존재함 그리고 요금이 멤버쉽 요금으로 치환됐음
  isMemberFare?: boolean;
  // 멥버쉽 요금이 존재하지만 비로그인 상태인경우 노출하지 않는다
  hideMemberFare?: boolean;
  Org?: IFareDetail;
  isFreeSameDayRefundable?: boolean;
  isHoneymoonBenefit?: boolean;
}

export interface IFareTypeList {
  [key: string]: IFareDetail[];
}

export interface ISchFare {
  sch: string[];
  fare: IFareTypeList;
}

export interface ISchFares {
  [key: string]: ISchFare;
}

export type SchFaresMap = Map<string, IFareTypeList>;

export interface IError {
  agtCode: string;
  errCode: number;
  errMsg: string;
}

export interface IInternationalItem {
  airlines: IKeyStringValue;
  airports: IKeyStringValue;
  fareTypes: IKeyStringValue;
  schedules: ISchedules[];
  fares: ISchFares;
  errors: IError[];
}

interface IReceivedInternationalList {
  airlines: IKeyStringValue;
  airports: IKeyStringValue;
  fareTypes: IKeyStringValue;
  schedules: ISchedules[];
  fares: ISchFares;
  errors: IError[];
  replacedFares?: ISchFares;
  carbonEmissionAverage: ICarbonEmissionAverage;
}

export interface IReceivedInternationalResult {
  galileoFlag: boolean;
  galileoKey: string;
  travelBizFlag: boolean;
  travelBizKey: string;
  resCnt: number;
  totalResCnt: number;
  results: IReceivedInternationalList;
}

export interface IExpressingInternationalList {
  airlines: IKeyStringValue;
  airports: IKeyStringValue;
  fareTypes: IKeyStringValue;
  schedules: ISchedules[];
  schs: string[];
  fares: SchFaresMap;
  errors: IError[];
}

export interface ISortOption {
  key: SortKeys;
  order: SortOrders;
  targetIndex: number;
}

export enum SortKeys {
  /* 공통 */
  price = 'price', // 가격
  date = 'date', // 출발시각
  duration = 'duration', // 소요시간
  name = 'name', // 항공사 이름

  /* 왕복 전용 */
  depDate = 'depDate', // 출발시각 가는편
  arrDate = 'arrDate', // 출발시각 오는편
  depDuration = 'depDuration', // 소요시간 가는편
  arrDuration = 'arrDuration', // 소요시간 오는편
}

export enum SortOrders {
  ascending = 1,
  descending = -1,
}

export interface IInternationalContextResult {
  airlines?: IKeyStringValue;
  airports?: IKeyStringValue;
  completed: boolean;
  error: any;
  localErrors: IError[];
  fareTypes?: IKeyStringValue;
  fares: SchFaresMap;
  itinerary: IItinerary[];
  naverFarePromotion: INaverFarePromotion;
  resCnt: number;
  totalResCnt: number;
  schedules?: ISchedules[];
  schs: string[];
  carbonEmissionAverage: ICarbonEmissionAverage;
}

export interface IMarks {
  [key: number]: {
    style: IKeyAnyValue;
    label: string;
  };
}

export interface INaverFarePromotion {
  isAdultDiscount: boolean;
  adultDiscountPrice: number;
  isChildDiscount: boolean;
  childDiscountPrice: number;
}
